import { ClaimAvatarQuery } from '@/__generated__/ClaimAvatarQuery.graphql'
import { MenuLink } from '@/components/Header/MenuLink'
import { WalletAvatar } from '@/components/Share/WalletAvatar'
import { useWalletAuth } from '@/hooks/useWalletAuth'
import { useWallets } from '@/hooks/useWallets'
import { shortenAddress } from '@ourzora/utils'
import { usePrivy } from '@privy-io/react-auth'
import React, { Suspense } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import {
  Button,
  Copy,
  Icon,
  Info,
  Popover,
  SignOut,
  Skeleton,
  Text,
  Tooltip,
  View,
} from 'reshaped'
import { useCopyToClipboard } from 'usehooks-ts'
import { Avatar } from '../Claim/Avatar'
import styles from './MetallicBorder.module.css'

const query = graphql`
  query ClaimAvatarQuery($identifier: String!) {
    profile(identifier: $identifier) {
      id
      handle
      avatar {
        icon
        small
        blurhash
      }
      ...AvatarFragment
    }
  }
`

function ConnectedAvatar({ walletAddress }: { walletAddress: string }) {
  const data = useLazyLoadQuery<ClaimAvatarQuery>(query, { identifier: walletAddress })
  const { logout } = useWalletAuth()
  const { user } = usePrivy()
  const [_, copy] = useCopyToClipboard()

  const isEmbeddedWallet = user?.linkedAccounts?.some(
    (account) =>
      account.type === 'wallet' &&
      account.walletClientType === 'privy' &&
      account.address === walletAddress
  )

  const walletType = isEmbeddedWallet ? 'Embedded wallet' : 'External wallet'
  const tooltipText = isEmbeddedWallet
    ? 'Created when you first signed up on Zora.'
    : 'You created this using platforms like Rainbow, Zerion or MetaMask.'

  return (
    <Popover>
      <Popover.Trigger>
        {(attributes) => (
          <Button variant="ghost" attributes={attributes}>
            <View borderRadius="circular" className={styles.metallicBorder}>
              {data.profile ? (
                <Avatar avatarKey={data.profile} size={8} />
              ) : (
                <WalletAvatar />
              )}
            </View>
          </Button>
        )}
      </Popover.Trigger>
      <Popover.Content>
        <View padding={2} gap={2}>
          <View direction="row" align="center" gap={3}>
            <View borderRadius="circular" className={styles.metallicBorder}>
              {data.profile ? (
                <Avatar avatarKey={data.profile} size={10} />
              ) : (
                <WalletAvatar size={10} />
              )}
            </View>
            <View gap={0.5}>
              <Text weight="semibold" variant="body-3">
                {data.profile?.handle || shortenAddress(walletAddress)}
              </Text>
              <View direction="row" gap={0.5} align="center">
                <Text color="neutral-faded" variant="caption-1">
                  {walletType}
                </Text>
                <Tooltip text={tooltipText}>
                  {(attributes) => (
                    <Icon
                      svg={Info}
                      size={4}
                      color="neutral-faded"
                      attributes={attributes}
                    />
                  )}
                </Tooltip>
              </View>
            </View>
          </View>
        </View>
        <MenuLink
          icon={<Icon svg={Copy} size={6} color="primary" />}
          onClick={() => copy(walletAddress)}
        >
          Copy address
        </MenuLink>
        <MenuLink icon={<Icon svg={SignOut} size={6} color="primary" />} onClick={logout}>
          Disconnect
        </MenuLink>
      </Popover.Content>
    </Popover>
  )
}

export function ClaimAvatar() {
  const { hasConnectedWallet } = useWalletAuth()
  const { wallets } = useWallets()

  if (hasConnectedWallet && wallets.length > 0) {
    const [primaryWalletAddress] = wallets[0]

    return (
      <Suspense fallback={<Skeleton width={8} height={8} borderRadius="circular" />}>
        <ConnectedAvatar walletAddress={primaryWalletAddress} />
      </Suspense>
    )
  }

  return null
}
